'use client';

import Link from '@/components/i18n/LocaleLink';
import Image from 'next/image';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import { useRouter } from '@/hooks/use-locale-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, ArrowLeft, Eye, EyeOff, X, Mail, KeyRound, RefreshCw, Building2, ImageIcon, FileText, Users, Zap, Settings, Globe, CreditCard, BarChart3, Clock, Check, AlertCircle } from 'lucide-react';
import { analytics, AnalyticsEvents, identifyUser } from '@/lib/analytics';
import { type Locale, LOCALE_COOKIE, DEFAULT_LOCALE } from '@/lib/i18n';
import { getTranslations } from '@/lib/i18n/translations';
import { localizedHref } from '@/lib/i18n/href';
import { SocialAuthButtons, type OAuthBeginResult } from './social-auth-buttons';
import { HelpLanguageMenu } from '@/app/help/_components/HelpLanguageMenu';
import { HELP_LOCALES, HELP_T } from '@/app/help/_components/help-header-i18n';
import { plural } from '@/lib/i18n/plural';
import { authBackendMessage, getAccountFlowsCopy, postAccountFlow } from './account-flows-copy';

type UserType = 'admin' | 'team' | 'member' | 'user' | 'marketing';

function getRedirectByRole(userType: UserType): string {
  switch (userType) {
    case 'admin': return '/admin';
    case 'team': return '/dashboard';
    case 'member': return '/workspace';
    case 'user': return '/dashboard'; // Freelancers go to dashboard
    case 'marketing': return '/admin/blog';
    default: return '/dashboard';
  }
}

/**
 * Whitelist-based redirect validation to prevent open-redirect attacks.
 * Only accepts relative paths pointing at routes we own. Anything else
 * (absolute URLs, protocol-relative URLs, schemes) is ignored so a
 * malicious link like `/sign-in?redirect=https://phishing.com` can't
 * bounce the user off-site after login.
 */
const SAFE_REDIRECT_PREFIXES = ['/dashboard', '/workspace', '/admin', '/myterms', '/corpus'];

function sanitizeRedirect(raw: string | null): string | null {
  if (!raw) return null;
  // Reject absolute URLs and protocol-relative URLs (//evil.com/path)
  if (raw.startsWith('//') || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) return null;
  // Must be a relative path starting with /
  if (!raw.startsWith('/')) return null;
  // Must begin with one of our allowed app prefixes
  if (!SAFE_REDIRECT_PREFIXES.some(p => raw === p || raw.startsWith(p + '/') || raw.startsWith(p + '?'))) {
    return null;
  }
  return raw;
}

/**
 * Textos de los dos campos nuevos del registro (idioma y código de campaña).
 *
 * Mapa inline por la misma razón que `HELP_T` en app/help/: son ocho strings
 * y los diccionarios de `lib/i18n` pesan ~85 KB por idioma.
 *
 * TODO(i18n): si estos textos crecen o se reutilizan fuera del registro,
 * moverlos al grupo `auth` de lib/i18n/index.ts y a los 5 diccionarios.
 */
const SIGNUP_EXTRA_T: Record<string, {
  languageLabel: string;
  codeLabel: string; codeOptional: string; codePlaceholder: string; codeApply: string;
  codeEmpty: string; codeNotFound: string; codeUnavailable: string; codeInstead: string;
  stepContinue: string; stepBack: string;
  benefitTrial: string; benefitDiscount: string;
}> = {
  es: {
    languageLabel: 'Idioma de la plataforma y comunicaciones',
    codeLabel: '¿Tienes un código?', codeOptional: 'opcional',
    codePlaceholder: 'Escríbelo aquí', codeApply: 'Aplicar',
    codeEmpty: 'Escribe un código primero',
    codeNotFound: 'No encontramos ese código. Revisa que esté bien escrito',
    codeUnavailable: 'No pudimos comprobar el código. Puedes continuar sin él',
    codeInstead: 'en vez de',
    stepContinue: 'Continuar', stepBack: 'Volver a los datos',
    benefitTrial: '{d} días y {c} créditos', benefitDiscount: 'El descuento o créditos extra del plan se escriben en Stripe al suscribirte. Canjearlo ahora gasta tu único código de por vida.',
  },
  en: {
    languageLabel: 'Platform and communications language',
    codeLabel: 'Have a code?', codeOptional: 'optional',
    codePlaceholder: 'Enter it here', codeApply: 'Apply',
    codeEmpty: 'Enter a code first',
    codeNotFound: "We couldn't find that code. Check the spelling",
    codeUnavailable: "We couldn't check the code. You can continue without it",
    codeInstead: 'instead of',
    stepContinue: 'Continue', stepBack: 'Back to your details',
    benefitTrial: '{d} days and {c} credits', benefitDiscount: 'Plan discount or extra plan credits are typed on Stripe when you subscribe. Using the code now spends your one lifetime redemption.',
  },
  pt: {
    languageLabel: 'Idioma da plataforma e comunicações',
    codeLabel: 'Tem um código?', codeOptional: 'opcional',
    codePlaceholder: 'Escreva aqui', codeApply: 'Aplicar',
    codeEmpty: 'Escreva um código primeiro',
    codeNotFound: 'Não encontrámos esse código. Verifique se está bem escrito',
    codeUnavailable: 'Não foi possível verificar o código. Pode continuar sem ele',
    codeInstead: 'em vez de',
    stepContinue: 'Continuar', stepBack: 'Voltar aos dados',
    benefitTrial: '{d} dias e {c} créditos', benefitDiscount: 'O desconto ou créditos extra do plano digitam-se no Stripe ao subscrever. Usar o código agora gasta o único resgate da conta.',
  },
  de: {
    languageLabel: 'Sprache der Plattform und Kommunikation',
    codeLabel: 'Haben Sie einen Code?', codeOptional: 'optional',
    codePlaceholder: 'Hier eingeben', codeApply: 'Anwenden',
    codeEmpty: 'Bitte zuerst einen Code eingeben',
    codeNotFound: 'Diesen Code konnten wir nicht finden. Bitte Schreibweise prüfen',
    codeUnavailable: 'Der Code konnte nicht geprüft werden. Sie können ohne ihn fortfahren',
    codeInstead: 'statt',
    stepContinue: 'Weiter', stepBack: 'Zurück zu den Daten',
    benefitTrial: '{d} Tage und {c} Guthaben', benefitDiscount: 'Tarif-Rabatt oder Extra-Credits werden bei Stripe beim Abo eingegeben. Jetzt einlösen verbraucht den einmaligen Code.',
  },
  fr: {
    languageLabel: 'Langue de la plateforme et communications',
    codeLabel: 'Vous avez un code ?', codeOptional: 'facultatif',
    codePlaceholder: 'Saisissez-le ici', codeApply: 'Appliquer',
    codeEmpty: "Saisissez d'abord un code",
    codeNotFound: "Nous n'avons pas trouvé ce code. Vérifiez l'orthographe",
    codeUnavailable: "Impossible de vérifier le code. Vous pouvez continuer sans",
    codeInstead: 'au lieu de',
    stepContinue: 'Continuer', stepBack: 'Revenir aux informations',
    benefitTrial: '{d} jours et {c} crédits', benefitDiscount: 'La réduction ou les crédits extra du forfait se saisissent sur Stripe à l’abonnement. L’utiliser maintenant consomme votre unique code à vie.',
  },
};

export function Login({ mode = 'signin', initialLocale }: { mode?: 'signin' | 'signup'; initialLocale?: Locale }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const redirect = sanitizeRedirect(searchParams.get('redirect'));
  const success = searchParams.get('success');
  const reason = searchParams.get('reason');
  const [showInactivityBanner, setShowInactivityBanner] = useState(reason === 'inactivity');

  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Auto-dismiss error toast after 5 seconds
  useEffect(() => {
    if (!error) return;
    const sticky = /origen|origin|JavaScript|Client ID|googleusercontent/i.test(error);
    const timer = setTimeout(() => setError(''), sticky ? 20000 : 5000);
    return () => clearTimeout(timer);
  }, [error]);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState<'email' | 'code' | 'password' | 'success'>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState(['', '', '', '', '', '']);
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
  const [forgotPending, setForgotPending] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Signup email-OTP step. After /auth/register the account does NOT exist yet
  // (the backend stored a pending registration + emailed a code); we show this
  // modal to collect the code, and /auth/verify-signup is what actually creates
  // the account and returns the auth token.
  const [signupStep, setSignupStep] = useState<'form' | 'code'>('form');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupCode, setSignupCode] = useState(['', '', '', '', '', '']);
  const [signupPending, setSignupPending] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [signupCooldown, setSignupCooldown] = useState(0);

  // --- Código de campaña (HU-B1) ---------------------------------------
  // El campo es OPCIONAL y quien no tiene código no debe percibir que le
  // falta algo. La validación es en vivo contra el backend; el código viaja
  // en /auth/register, se guarda en pending_registrations y se aplica en
  // createAccountFromRegistration, ya con el correo verificado por OTP.
  // El registro se parte en dos pasos: primero los datos de la cuenta,
  // despues idioma y codigo. Asi el formulario entra en una pantalla de
  // portatil sin apretar los campos, y los dos anadidos no compiten por
  // atencion con el correo y la contrasena.
  const [signupPart, setSignupPart] = useState<'main' | 'extras'>('main');
  const [oauthPending, setOauthPending] = useState<OAuthBeginResult | null>(null);
  const [campaignCode, setCampaignCode] = useState('');
  const [codeState, setCodeState] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [codeMessage, setCodeMessage] = useState('');
  // Confirmación COMPARATIVA: qué recibe con el código frente a qué recibiría
  // sin él. Un "código aplicado" a secas no comunica ningún valor.
  const [codeBenefit, setCodeBenefit] = useState<{
    trialDays: number; trialCredits: number;
    baseTrialDays: number; baseTrialCredits: number;
    discountPercent: number | null; discountMonths: number | null;
    purchaseCreditMultiplier?: number | null;
  } | null>(null);

  // i18n: seeded server-side via initialLocale prop to avoid an English flash,
  // and kept in sync client-side by reading the cookie (useful if the user
  // changes language on another tab while this one is open).
  const [locale, setLocale] = useState<Locale>(initialLocale || DEFAULT_LOCALE);
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const match = document.cookie.match(new RegExp(`${LOCALE_COOKIE}=([^;]+)`));
      if (match && ['en', 'es', 'pt', 'de', 'fr'].includes(match[1])) {
        setLocale(match[1] as Locale);
      }
    }
  }, []);
  // Al cambiar de idioma con el selector, la URL pasa a /{otro}/sign-in en
  // una navegación de cliente: el componente no se vuelve a montar y el
  // servidor no siempre vuelve a pintar la página. Se sigue al prefijo de la
  // URL (solo cuando cambia, para no pisar la cookie al montar).
  const pathname = usePathname() || '/';
  const urlSeg = pathname.split('/', 2)[1];
  const urlLocale = (HELP_LOCALES as readonly string[]).includes(urlSeg) ? (urlSeg as Locale) : null;
  const urlLocaleRef = useRef(urlLocale);
  useEffect(() => {
    if (urlLocale && urlLocale !== urlLocaleRef.current) {
      urlLocaleRef.current = urlLocale;
      setLocale(urlLocale);
    }
  }, [urlLocale]);
  // Selector de idioma: la misma pantalla en cada idioma, conservando la
  // query (redirect, plan, código de campaña…).
  const languageHrefs = useMemo(() => {
    const barePath = urlLocale ? pathname.slice(urlLocale.length + 1) || '/' : pathname;
    const query = searchParams.toString();
    return Object.fromEntries(
      HELP_LOCALES.map((l) => [l, `${localizedHref(barePath, l)}${query ? `?${query}` : ''}`]),
    ) as Record<Locale, string>;
  }, [pathname, urlLocale, searchParams]);
  // Fechas de ejemplo del panel decorativo (febrero de 2026) en el idioma de
  // la pantalla. En UTC para que servidor y navegador pinten lo mismo.
  const mockupDate = (day: number, withYear = false) => {
    try {
      return new Intl.DateTimeFormat(locale === 'pt' ? 'pt-BR' : locale, {
        month: 'short',
        day: 'numeric',
        ...(withYear ? { year: 'numeric' as const } : {}),
        timeZone: 'UTC',
      }).format(Date.UTC(2026, 1, day));
    } catch {
      return `${day}/02${withYear ? '/2026' : ''}`;
    }
  };
  // Cifra de ejemplo del panel decorativo con el separador del idioma
  // («1.247» en alemán, «1 247» en francés).
  const mockupNumber = (n: number) => {
    try {
      return new Intl.NumberFormat(locale === 'pt' ? 'pt-BR' : locale).format(n);
    } catch {
      return String(n);
    }
  };
  // The extras-step language buttons use this local state (not LanguageContext
  // setLocale) so picking a language does not navigate away and wipe the form.
  // We still have to persist the cookie here: Settings and the post-OTP
  // redirect read ezdocu-locale / the URL prefix, not users.platform_language.
  const persistSignupLocale = (next: Locale) => {
    setLocale(next);
    if (typeof document === 'undefined') return;
    document.cookie = `${LOCALE_COOKIE}=${next};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
  };
  const t = getTranslations(locale).auth;
  const flowCopy = getAccountFlowsCopy(locale);
  const tc = SIGNUP_EXTRA_T[locale] ?? SIGNUP_EXTRA_T.en;

  const handleOAuthBegin = async (result: OAuthBeginResult) => {
    setOauthPending(result);
    setError('');
    if (mode === 'signin' && result.existingUser) {
      setPending(true);
      try {
        const res = await fetch('/api/auth/oauth/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pendingToken: result.pendingToken, locale, mode: 'signin' }),
        });
        const data = await res.json();
        if (data.status === 200 && data.token) {
          await finishAuth({ ...data, method: 'oauth' });
          return;
        }
        setError(data.message || t.oauthFailed);
      } catch {
        setError(t.oauthFailed);
      } finally {
        setPending(false);
      }
      return;
    }
    // Login with an email Google/LinkedIn has never seen: same as signup
    // step 2 (locale + campaign code). The extras UI only mounts on /sign-up.
    if (mode === 'signin' && !result.existingUser) {
      try {
        sessionStorage.setItem('oauth_pending_begin', JSON.stringify(result));
      } catch { /* private mode */ }
      window.location.replace('/sign-up?oauth=pending');
      return;
    }
    setSignupPart('extras');
  };

  useEffect(() => {
    const flag = searchParams.get('oauth');
    if (flag === 'error') {
      setError(t.oauthFailed);
      return;
    }
    if (flag !== 'linkedin' && flag !== 'google' && flag !== 'pending') return;

    const stored = (key: string) => {
      try { return sessionStorage.getItem(key); } catch { return null; }
    };
    const clear = (key: string) => {
      try { sessionStorage.removeItem(key); } catch { /* */ }
    };

    if (flag === 'pending') {
      const raw = stored('oauth_pending_begin');
      clear('oauth_pending_begin');
      if (!raw) {
        setError(t.oauthFailed);
        return;
      }
      try {
        const data = JSON.parse(raw) as OAuthBeginResult;
        if (!data.pendingToken) {
          setError(t.oauthFailed);
          return;
        }
        void handleOAuthBegin(data);
      } catch {
        setError(t.oauthFailed);
      }
      return;
    }

    const code = flag === 'linkedin' ? stored('oauth_linkedin_code') : null;
    const idToken = flag === 'google' ? stored('oauth_google_id_token') : null;
    if (flag === 'linkedin') clear('oauth_linkedin_code');
    if (flag === 'google') clear('oauth_google_id_token');

    if ((flag === 'linkedin' && !code) || (flag === 'google' && !idToken)) {
      setError(t.oauthFailed);
      return;
    }

    (async () => {
      setPending(true);
      try {
        const res = await fetch(flag === 'google' ? '/api/auth/oauth/google' : '/api/auth/oauth/linkedin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(flag === 'google' ? { idToken } : { code }),
        });
        const data = await res.json();
        if (!data.pendingToken) {
          setError(data.message || t.oauthFailed);
          return;
        }
        await handleOAuthBegin(data);
      } catch {
        setError(t.oauthFailed);
      } finally {
        setPending(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Valida el código contra el backend y arma la confirmación comparativa.
   * No bloquea el registro: si falla, el usuario puede seguir sin código.
   */
  const handleApplyCode = async (override?: string) => {
    const code = (override ?? campaignCode).trim().toUpperCase();
    if (!code) {
      setCodeState('invalid');
      setCodeMessage(tc.codeEmpty);
      setCodeBenefit(null);
      return;
    }

    setCodeState('checking');
    setCodeMessage('');
    setCodeBenefit(null);

    try {
      const res = await fetch('/api/codes/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, email: signupEmail || undefined }),
      });
      const data = await res.json();

      if (res.ok && data.valid) {
        setCampaignCode(code);
        setCodeState('valid');
        setCodeMessage('');
        setCodeBenefit(data.benefit ?? null);
      } else {
        setCodeState('invalid');
        // El backend manda el motivo en lenguaje natural ("Este código venció
        // el 21 de mayo"), no un código técnico. Pero un 5xx trae mensajes de
        // infraestructura ("Internal server error") que no significan nada
        // para quien se está registrando: en ese caso se usa el genérico.
        const upstreamUsable = res.status < 500 && typeof data?.message === 'string' && data.valid === false;
        setCodeMessage(upstreamUsable ? data.message : tc.codeUnavailable);
        setCodeBenefit(null);
      }
    } catch {
      setCodeState('invalid');
      setCodeMessage(tc.codeUnavailable);
      setCodeBenefit(null);
    }
  };

  // Un link compartido ("usa el código X y obtén Y") trae `?code=` en la URL.
  // Se precarga y se valida solo, para que quien llega desde ese link vea la
  // confirmación del beneficio sin tener que volver a escribir el código.
  // Una sola vez: si la persona lo borra a mano, no se lo reimponemos.
  useEffect(() => {
    if (mode !== 'signup') return;
    const fromUrl = searchParams.get('code');
    if (!fromUrl) return;
    const normalized = fromUrl.trim().toUpperCase();
    if (!normalized) return;
    setCampaignCode(normalized);
    handleApplyCode(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Password real-time validation (signup only)
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);

  const pwdChecks = {
    minLength: passwordValue.length >= 8,
    uppercase: /[A-Z]/.test(passwordValue),
    lowercase: /[a-z]/.test(passwordValue),
    number: /[0-9]/.test(passwordValue),
  };

  const pwdScore = Object.values(pwdChecks).filter(Boolean).length;
  const allPwdValid = pwdScore === 4;

  const getStrengthColor = () => {
    if (pwdScore <= 1) return 'bg-red-500';
    if (pwdScore === 2) return 'bg-orange-500';
    if (pwdScore === 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStrengthLabel = () => {
    if (pwdScore <= 1) return t.pwdStrengthWeak;
    if (pwdScore === 2) return t.pwdStrengthFair;
    if (pwdScore === 3) return t.pwdStrengthGood;
    return t.pwdStrengthStrong;
  };

  // Parse backend error messages (handles NestJS format: string, array of strings, array of objects)
  const parseErrorMessage = (message: unknown): string => {
    if (typeof message === 'string') {
      // Map generic "Bad Request" to a clear message
      if (message === 'Bad Request') return t.authFailed;
      // NestJS @nestjs/throttler emits "ThrottlerException: Too Many Requests"
      // verbatim. Catch any of those shapes and surface our localized copy
      // instead of leaking the framework name to the user.
      if (/throttler|too many requests/i.test(message)) return t.tooManyRequests;
      // Localize the backend's English auth messages (they arrive verbatim
      // from auth-ms). The two "locked" variants carry a minute count we
      // re-inject via {n}.
      if (/^invalid credentials$/i.test(message)) return t.invalidCredentials;
      if (/^user already exists$/i.test(message)) return t.userExists;
      if (/has been deactivated/i.test(message)) return t.accountDeactivated;
      let locked = message.match(/temporarily locked.*?(\d+)\s*minute/i);
      if (locked) return plural(locale, Number(locked[1]), { one: t.accountLockedOne, other: t.accountLockedOther });
      locked = message.match(/too many failed attempts.*?(\d+)\s*minute/i);
      if (locked) return plural(locale, Number(locked[1]), { one: t.tooManyAttemptsLockedOne, other: t.tooManyAttemptsLockedOther });
    }
    // El resto (códigos, registro pendiente, validación de class-validator,
    // también en array) se traduce con la copia de los flujos de cuenta. Lo
    // que no se reconoce va a la consola y el usuario ve un genérico en su
    // idioma: el inglés del backend nunca llega a la pantalla.
    const traducido = authBackendMessage(flowCopy, message);
    if (traducido) return traducido;
    if (message) console.warn('Auth backend message without translation:', message);
    return typeof message === 'string' || Array.isArray(message) ? flowCopy.signup.errorGeneric : t.authFailed;
  };

  // Mockup screen cycling
  const [mockupScreen, setMockupScreen] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMockupScreen((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Company registration states
  const [isCompany, setIsCompany] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Sube el logo pendiente al backend (S3 canónico) con el JWT recién
  // emitido por verify-signup. Best-effort: un fallo no bloquea el registro.
  const uploadPendingLogo = async (data: any): Promise<void> => {
    if (!logoFile || !data?.token) return;
    const accId = data.account?.id || data.accounts?.[0]?.id;
    if (!accId) return;
    try {
      setUploadingLogo(true);
      const imageData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(logoFile);
      });
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const res = await fetch(`${apiUrl}/auth/accounts/${accId}/logo`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.token}` },
        body: JSON.stringify({ imageData }),
      });
      if (!res.ok) console.warn('Logo upload failed:', res.status);
    } catch (err) {
      console.warn('Logo upload failed:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle logo file selection
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(t.logoTooLarge);
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * Turn a successful auth response (login, or verify-signup which returns the
   * same { token, user, accounts } shape) into a session cookie + redirect.
   * Shared by sign-in and the signup OTP confirmation.
   */
  const finishAuth = async (data: any): Promise<boolean> => {
    if (!data?.token) return false;
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
    const userType = data.user?.roleName || data.roleName || 'member';
    const userRoleId = data.user?.roleId || data.roleId;

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userTimezone = data.user?.timezone && data.user.timezone !== 'UTC'
      ? data.user.timezone
      : browserTimezone;

    if (!data.user?.timezone || data.user.timezone === 'UTC') {
      fetch(`${apiUrl}/auth/update-timezone`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.token}` },
        body: JSON.stringify({ timezone: browserTimezone }),
      }).catch(() => {});
    }

    const sessionRes = await fetch('/api/auth/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user: data.user,
        userType,
        userRoleId,
        isPlatformAdmin: !!data.user?.isPlatformAdmin,
        accounts: data.accounts || [data.account],
        token: data.token,
        activeAccountId: data.account?.id || data.accounts?.[0]?.id,
        timezone: userTimezone,
        toursSeen: Array.isArray(data.user?.toursSeen) ? data.user.toursSeen : [],
      }),
    });

    if (!sessionRes.ok) {
      setError(t.sessionError);
      return false;
    }

    identifyUser(analytics, {
      id: data.user.id,
      email: data.user.email,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      userType: userType as 'admin' | 'team' | 'member' | 'user' | 'marketing',
      userRoleId,
      activeAccountId: data.account?.id || data.accounts?.[0]?.id,
      timezone: userTimezone,
      accountName: data.account?.name || data.accounts?.[0]?.name,
    });
    analytics.capture(
      mode === 'signin' ? AnalyticsEvents.SIGN_IN : AnalyticsEvents.SIGN_UP,
      { method: 'email', is_company: isCompany, user_type: userType }
    );

    // Clear stale inactivity timestamp so the dashboard doesn't immediately
    // log the user out on mount.
    try { localStorage.removeItem('lastActivityTimestamp'); } catch {}

    const targetUrl = redirect || getRedirectByRole(userType as UserType);
    // useLocaleRouter prefixes with the URL locale, which is still /en (or
    // whatever they landed on) even after they picked another language on
    // the extras step. Hard-nav to the selected locale so dashboard/Settings
    // match users.platform_language (LanguageContext reads the URL prefix).
    if (mode === 'signup') {
      window.location.assign(localizedHref(targetUrl, locale));
      return true;
    }
    // El idioma GUARDADO del usuario manda al entrar: aunque el sign-in se
    // haya abierto en /es (link viejo, cookie borrada, otro equipo), el
    // dashboard aterriza en el /{locale}/ de su preferencia y el proxy
    // re-siembra la cookie al pisar esa URL ("que se guarde por el usuario"
    // — Andrey, 10 sep 2026). Sin preferencia guardada, todo sigue igual.
    const idiomaGuardado = data.user?.platformLanguage as Locale | undefined;
    if (idiomaGuardado && ['en', 'es', 'pt', 'de', 'fr'].includes(idiomaGuardado) && idiomaGuardado !== locale) {
      document.cookie = `${LOCALE_COOKIE}=${idiomaGuardado};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
      window.location.assign(localizedHref(targetUrl, idiomaGuardado));
      return true;
    }
    router.push(targetUrl);
    router.refresh();
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setPending(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    // Client-side password validation for signup (OAuth already verified the email)
    if (mode === 'signup' && !oauthPending && !allPwdValid) {
      setError(t.passwordMinLength);
      setPending(false);
      return;
    }

    // Paso 1 → paso 2. El navegador ya valido los obligatorios al enviar
    // (el boton es type=submit a proposito), asi que aqui solo se avanza.
    // No se llama al backend todavia.
    if (mode === 'signup' && signupPart === 'main') {
      setSignupPart('extras');
      setPending(false);
      return;
    }

    try {
      if (oauthPending) {
        const res = await fetch('/api/auth/oauth/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pendingToken: oauthPending.pendingToken,
            locale,
            campaignCode: codeState === 'valid' && campaignCode ? campaignCode : undefined,
            accountName: isCompany ? (formData.get('accountName') as string) || undefined : undefined,
            mode: mode === 'signin' ? 'signin' : 'signup',
          }),
        });
        const data = await res.json();
        if ((data.status === 200 || data.status === 201) && data.token) {
          await finishAuth(data);
        } else {
          setError(parseErrorMessage(data.message) || t.oauthFailed);
        }
        return;
      }

      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const endpoint = mode === 'signin' ? '/auth/login' : '/auth/register';

      // locale → backend: alimenta platform_language/interface_language en HubSpot
      const body: Record<string, string | undefined> = { email, password, locale };

      if (mode === 'signup') {
        body.firstName = formData.get('firstName') as string;
        body.lastName = formData.get('lastName') as string;

        // Solo incluir accountName si es company. El logo NO se sube aquí:
        // queda en memoria y se sube autenticado a S3 tras verificar el OTP
        // (la subida anónima a Cloudinary fallaba en silencio y es legacy).
        if (isCompany) {
          body.accountName = formData.get('accountName') as string;
        }

        // Solo se manda si el backend lo validó. Un código escrito a medias
        // y nunca aplicado no debe bloquear el registro.
        if (codeState === 'valid' && campaignCode) {
          body.code = campaignCode;
        }
      }

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (mode === 'signup' && data.status === 200 && data.pendingVerification) {
        // Account not created yet — the backend emailed an OTP. Open the
        // verification step; /auth/verify-signup will finish the signup.
        analytics.capture(AnalyticsEvents.SIGN_UP, { method: 'email', step: 'code_sent', is_company: isCompany });
        setSignupEmail(email);
        setSignupCode(['', '', '', '', '', '']);
        setSignupError('');
        setSignupStep('code');
        setSignupCooldown(30);
      } else if ((mode === 'signin' && data.status === 200) || (mode === 'signup' && data.status === 201)) {
        await finishAuth(data);
      } else {
        setError(parseErrorMessage(data.message));
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(t.connectionError);
    } finally {
      setPending(false);
    }
  };

  // Send verification code
  const handleSendCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setForgotError('');
    setForgotPending(true);

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      // `locale`: el código llega en el idioma de esta pantalla (con
      // respaldo sin él si el backend aún no lo admite).
      const { status, data } = await postAccountFlow(`${apiUrl}/auth/forgot-password`, { email: forgotEmail, locale });

      // 429 from the gateway throttler can come with the framework's default
      // body shape ({statusCode, message}) instead of the wrapped one we
      // expect from the auth-ms TCP path. statusOf() already read both.
      if (status === 429) {
        setForgotError(t.tooManyRequests);
        return;
      }

      if (status === 200) {
        analytics.capture(AnalyticsEvents.PASSWORD_RESET_REQUESTED);
        setForgotStep('code');
        setResendCooldown(30);
      } else {
        setForgotError(parseErrorMessage(data?.message));
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotError(t.connectionError);
    } finally {
      setForgotPending(false);
    }
  };

  // Resend code
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setForgotPending(true);

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const { status, data } = await postAccountFlow(`${apiUrl}/auth/forgot-password`, { email: forgotEmail, locale });

      if (status === 429) {
        setForgotError(t.tooManyRequests);
        return;
      }

      if (status === 200) {
        setResendCooldown(30);
        setForgotCode(['', '', '', '', '', '']);
      } else {
        setForgotError(parseErrorMessage(data?.message));
      }
    } catch (err) {
      console.error('Resend error:', err);
    } finally {
      setForgotPending(false);
    }
  };

  // Handle code input (alphanumeric codes)
  const handleCodeChange = (index: number, value: string) => {
    const sanitize = (v: string) => v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    if (value.length > 1) {
      const chars = sanitize(value).slice(0, 6).split('');
      const newCode = [...forgotCode];
      chars.forEach((char, i) => {
        if (index + i < 6) {
          newCode[index + i] = char;
        }
      });
      setForgotCode(newCode);
    } else {
      const newCode = [...forgotCode];
      newCode[index] = sanitize(value);
      setForgotCode(newCode);
    }
  };

  // Verify code and go to password step
  const handleVerifyCode = () => {
    const codeString = forgotCode.join('');
    if (codeString.length !== 6) {
      setForgotError(t.verificationCode);
      return;
    }
    setForgotError('');
    setForgotStep('password');
  };

  // Reset password
  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setForgotError('');

    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError(t.passwordsNoMatch);
      return;
    }

    if (forgotNewPassword.length < 8) {
      setForgotError(t.passwordMinLength);
      return;
    }

    setForgotPending(true);

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const response = await fetch(`${apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotEmail,
          code: forgotCode.join(''),
          newPassword: forgotNewPassword,
        }),
      });

      if (response.status === 429) {
        setForgotError(t.tooManyRequests);
        return;
      }

      const data = await response.json();

      if (data.status === 200 || data.statusCode === 200) {
        analytics.capture(AnalyticsEvents.PASSWORD_RESET_COMPLETED);
        setForgotStep('success');
      } else {
        setForgotError(parseErrorMessage(data.message));
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setForgotError(t.connectionError);
    } finally {
      setForgotPending(false);
    }
  };

  // Reset modal state
  const resetForgotModal = () => {
    setShowForgotModal(false);
    setForgotStep('email');
    setForgotEmail('');
    setForgotCode(['', '', '', '', '', '']);
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotError('');
    setResendCooldown(0);
  };

  // ── Signup email-OTP handlers ──────────────────────────────────────────
  const handleSignupCodeChange = (index: number, value: string) => {
    const sanitize = (v: string) => v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (value.length > 1) {
      const chars = sanitize(value).slice(0, 6).split('');
      const newCode = [...signupCode];
      chars.forEach((char, i) => { if (index + i < 6) newCode[index + i] = char; });
      setSignupCode(newCode);
    } else {
      const newCode = [...signupCode];
      newCode[index] = sanitize(value);
      setSignupCode(newCode);
    }
  };

  const handleVerifySignup = async () => {
    const codeString = signupCode.join('');
    if (codeString.length !== 6) {
      setSignupError(t.verificationCode);
      return;
    }
    setSignupError('');
    setSignupPending(true);
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const response = await fetch(`${apiUrl}/auth/verify-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: signupEmail, code: codeString, locale }),
      });
      if (response.status === 429) {
        setSignupError(t.tooManyRequests);
        setSignupCode(['', '', '', '', '', '']); // clear so auto-submit can't loop
        return;
      }
      const data = await response.json();
      if (data.status === 201 && data.token) {
        await uploadPendingLogo(data);
        await finishAuth(data);
      } else {
        setSignupError(parseErrorMessage(data.message));
        setSignupCode(['', '', '', '', '', '']); // clear so auto-submit can't loop
      }
    } catch (err) {
      console.error('Verify signup error:', err);
      setSignupError(t.connectionError);
      setSignupCode(['', '', '', '', '', '']);
    } finally {
      setSignupPending(false);
    }
  };

  const handleResendSignupCode = async () => {
    if (signupCooldown > 0) return;
    setSignupPending(true);
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const response = await fetch(`${apiUrl}/auth/resend-signup-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: signupEmail }),
      });
      if (response.status === 429) {
        setSignupError(t.tooManyRequests);
        return;
      }
      const data = await response.json();
      if (data.status === 200) {
        setSignupCooldown(30);
        setSignupCode(['', '', '', '', '', '']);
      } else {
        setSignupError(parseErrorMessage(data.message));
      }
    } catch (err) {
      console.error('Resend signup code error:', err);
    } finally {
      setSignupPending(false);
    }
  };

  const closeSignupCode = () => {
    setSignupStep('form');
    setSignupCode(['', '', '', '', '', '']);
    setSignupError('');
    setSignupCooldown(0);
  };

  // Auto-dismiss inactivity banner after 6s
  useEffect(() => {
    if (showInactivityBanner) {
      const timer = setTimeout(() => setShowInactivityBanner(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [showInactivityBanner]);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Signup OTP resend cooldown timer
  useEffect(() => {
    if (signupCooldown > 0) {
      const timer = setTimeout(() => setSignupCooldown(signupCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [signupCooldown]);

  // Auto-submit the signup OTP as soon as all 6 digits are filled — no need to
  // click "Verify & continue". On a wrong code handleVerifySignup clears the
  // boxes, so this can't loop.
  useEffect(() => {
    if (signupStep === 'code' && !signupPending && signupCode.every((c) => c !== '')) {
      handleVerifySignup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupCode, signupStep]);

  // h-screen, no min-h-screen: la pagina NUNCA hace scroll.
  //
  // La columna del formulario SI lleva overflow-y-auto, y es necesario: al
  // enfocar la contrasena aparece el checklist de requisitos, que anade unos
  // 90px y dejaba el boton fuera de pantalla sin forma de alcanzarlo. No lo
  // quites: sin el, en un portatil de 14" no se puede completar el registro.
  return (
    <div className="h-screen overflow-hidden flex relative">
      {/* Inactivity push notification banner */}
      {showInactivityBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-4 px-4 animate-in slide-in-from-top duration-300">
          <div className="bg-white border border-gray-200 shadow-lg rounded-lg px-5 py-3 flex items-center gap-3 whitespace-nowrap">
            <Clock className="w-4 h-4 text-purple-600 shrink-0" />
            <span className="text-sm text-gray-800">{t.sessionExpiredDesc}</span>
            <button
              onClick={() => setShowInactivityBanner(false)}
              className="ml-auto text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Left side - Form */}
      <div className="w-full lg:w-[42%] xl:w-[38%] flex flex-col overflow-y-auto px-4 sm:px-6 lg:px-12 xl:px-16">
        {/* Back to home - Fixed at top */}
        {/* Mismo ancho y centrado que el formulario, para que el enlace quede
            alineado con el bloque en vez de pegado al borde de la columna. */}
        <div className="w-full max-w-sm mx-auto pt-5">
          <div className="flex items-center justify-between gap-3">
            <Link href="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {t.backToHome}
            </Link>
            <HelpLanguageMenu
              currentLocale={locale}
              hrefByLocale={languageHrefs}
              label={(HELP_T[locale] ?? HELP_T.en).language}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center pt-7 pb-4">
          <div className="w-full max-w-sm mx-auto">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 mb-4">
            <Image src="/images/brand/ezdocuai-logo.png" alt="EZDocuAI" width={140} height={36} className="h-9 w-auto" />
          </Link>

          {/* Header */}
          <div className="mb-4">
            <h1 className="text-lg font-semibold text-gray-900">
              {mode === 'signin' ? t.welcomeBack : t.createAccount}
            </h1>
            <p className="text-gray-500 text-xs mt-1">
              {mode === 'signin'
                ? t.signInSubtitle
                : t.startFreeTrial}
            </p>
          </div>

{success && (
            <div className="mb-6 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              <CheckCircle className="w-4 h-4" />
              {t.accountCreatedSuccess}
            </div>
          )}

          {/* Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Paso 1 · datos de la cuenta. Se OCULTA en el paso 2, no se
                desmonta: si se desmontara, sus valores saldrian del FormData
                y el registro se enviaria sin nombre ni correo. */}
            {/* space-y-8 solo alcanza a los hijos DIRECTOS del form, y este
                envoltorio (display:contents) rompia esa cadena: los campos de
                dentro quedaban sin separacion. Por eso lo repite. */}
            <div className={mode === 'signup' && signupPart === 'extras' ? 'hidden' : 'contents space-y-6'}>
            {mode === 'signup' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName" className="text-xs font-medium text-gray-700">
                      {t.firstName}
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required={!oauthPending}
                      className="mt-3 h-11 rounded-lg border-gray-200 focus:border-gray-900 focus:ring-gray-900"
                      placeholder={t.firstNamePlaceholder}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-xs font-medium text-gray-700">
                      {t.lastName}
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required={!oauthPending}
                      className="mt-3 h-11 rounded-lg border-gray-200 focus:border-gray-900 focus:ring-gray-900"
                      placeholder={t.lastNamePlaceholder}
                    />
                  </div>
                </div>

                {/* Company Checkbox */}
                {/* La casilla es un modificador de los datos de arriba, no un
                    grupo propio: se pega al bloque de nombre en vez de heredar la
                    separacion de space-y-8: va pegada. El ! hace falta porque space-y aplica
                    su margin-top con selector de hermanos. */}
                <div className="mt-0! flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isCompany"
                    checked={isCompany}
                    onChange={(e) => {
                      setIsCompany(e.target.checked);
                      if (!e.target.checked) {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <label htmlFor="isCompany" className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-600">
                    <Building2 className="w-4 h-4" />
                    {t.registerAsCompany}
                  </label>
                </div>

                {/* Company Fields - Only show if isCompany */}
                {isCompany && (
                  <div className="flex gap-3 items-start">
                    {/* Logo Upload - Compact */}
                    <div className="flex-shrink-0">
                      {logoPreview ? (
                        <div className="relative w-[72px] h-[72px] rounded-lg overflow-hidden border border-purple-200">
                          <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label
                          htmlFor="logo-upload"
                          className="flex flex-col items-center justify-center w-[72px] h-[72px] border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 transition-colors"
                        >
                          <ImageIcon className="w-5 h-5 text-gray-400" />
                          <span className="text-[10px] text-gray-400 mt-1">Logo</span>
                          <input id="logo-upload" type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                        </label>
                      )}
                    </div>
                    {/* Company Name */}
                    <div className="flex-1">
                      <Label htmlFor="accountName" className="text-xs font-medium text-gray-700">{t.companyName}</Label>
                      <Input
                        id="accountName"
                        name="accountName"
                        type="text"
                        required={isCompany}
                        className="mt-1 h-11 rounded-lg border-gray-200 focus:border-purple-500 focus:ring-purple-500"
                        placeholder="Acme Translations LLC"
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            <div>
              <Label htmlFor="email" className="text-xs font-medium text-gray-700">
                {t.emailAddress}
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required={!oauthPending}
                className="mt-3 h-11 rounded-lg border-gray-200 focus:border-gray-900 focus:ring-gray-900"
                placeholder={t.emailPlaceholder}
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs font-medium text-gray-700">
                {t.password}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  required={!oauthPending}
                  minLength={oauthPending ? undefined : 8}
                  className="mt-3 h-11 rounded-lg border-gray-200 focus:border-gray-900 focus:ring-gray-900 pr-12"
                  placeholder={mode === 'signin' ? t.enterPassword : t.passwordPlaceholder}
                  value={mode === 'signup' ? passwordValue : undefined}
                  onChange={mode === 'signup' ? (e) => setPasswordValue(e.target.value) : undefined}
                  onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                  onPaste={(e) => {
                    const t = e.clipboardData.getData('text');
                    if (/\s/.test(t)) { e.preventDefault(); document.execCommand('insertText', false, t.replace(/\s+/g, '')); }
                  }}
                  onFocus={mode === 'signup' ? () => setPasswordFocused(true) : undefined}
                  onBlur={mode === 'signup' ? () => setPasswordFocused(false) : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Password requirements checklist - signup only */}
              {mode === 'signup' && (passwordFocused || passwordValue.length > 0) && (
                <div className="mt-3 space-y-1.5">
                  {[
                    { key: 'minLength' as const, label: t.pwdMinLength },
                    { key: 'uppercase' as const, label: t.pwdUppercase },
                    { key: 'lowercase' as const, label: t.pwdLowercase },
                    { key: 'number' as const, label: t.pwdNumber },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      {pwdChecks[key] ? (
                        <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-red-400 shrink-0" />
                      )}
                      <span className={pwdChecks[key] ? 'text-green-600' : 'text-red-500'}>
                        {label}
                      </span>
                    </div>
                  ))}

                  {/* Strength bar */}
                  {passwordValue.length > 0 && (
                    <div className="pt-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${getStrengthColor()}`}
                            style={{ width: `${(pwdScore / 4) * 100}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${pwdScore <= 1 ? 'text-red-500' : pwdScore === 2 ? 'text-orange-500' : pwdScore === 3 ? 'text-yellow-600' : 'text-green-500'}`}>
                          {getStrengthLabel()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>
            {/* Paso 2 · idioma y codigo. Solo en el registro y solo tras
                completar los datos de la cuenta. */}
            {(mode !== 'signup' || signupPart === 'extras') && (
            <>
            {/* ── Idioma (entregable D) ──────────────────────────────────
                Escribe la MISMA variable que el selector "Interface Language"
                de Ajustes: viaja como `locale` en /auth/register y acaba en
                users.platform_language. Por eso la respuesta cambia también
                el idioma de la interfaz, no solo el de las comunicaciones. */}
            {mode === 'signup' && (
              <div>
                <span className="block text-xs font-medium text-gray-700">
                  {tc.languageLabel}
                </span>
{/* Selector segmentado en vez de <select>: con dos opciones un
                    desplegable sobra, y ademas la lista desplegada de un select
                    nativo la dibuja el sistema operativo y NO se puede estilar
                    (ese azul de Windows no sale con CSS). Mismo patron que el
                    selector de ciclo en la pantalla de codigos. */}
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {([['es', 'Español'], ['en', 'English']] as const).map(([code, label]) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => persistSignupLocale(code as Locale)}
                      aria-pressed={locale === code}
                      className={`h-11 rounded-lg border text-sm transition-colors ${
                        locale === code
                          ? 'border-purple-400 bg-purple-50 font-medium text-purple-700'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Código de campaña (entregable C · HU-B1) ───────────────
                Bajo una línea divisoria y marcado como opcional: quien no
                tiene código no debe percibir que le falta algo. */}
            {mode === 'signup' && (
              <div className="border-t border-gray-100 pt-4">
                <Label htmlFor="signup-code" className="text-xs font-medium text-gray-700">
                  {tc.codeLabel}{' '}
                  <span className="font-normal text-gray-400">{tc.codeOptional}</span>
                </Label>
                <div className="mt-3 flex gap-2">
                  <Input
                    id="signup-code"
                    name="campaignCode"
                    value={campaignCode}
                    onChange={(e) => {
                      // Se normaliza en vivo: mayúsculas y sin espacios.
                      setCampaignCode(e.target.value.toUpperCase().replace(/\s/g, ''));
                      // Escribir de nuevo invalida la validación anterior; un
                      // segundo código reemplaza al primero, nunca se acumulan.
                      setCodeState('idle');
                      setCodeMessage('');
                      setCodeBenefit(null);
                    }}
                    className="h-11 flex-1 rounded-lg border-gray-200 font-mono uppercase focus:border-gray-900 focus:ring-gray-900"
                    placeholder={tc.codePlaceholder}
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleApplyCode()}
                    disabled={codeState === 'checking'}
                    className="h-11 rounded-lg px-5"
                  >
                    {codeState === 'checking'
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : tc.codeApply}
                  </Button>
                </div>

                {/* Confirmación comparativa: qué recibe frente a qué recibiría
                    sin el código. Es lo que comunica el valor. */}
                {codeState === 'valid' && codeBenefit && (
                  <div className="mt-3 rounded-lg bg-green-50 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 shrink-0 text-green-600" />
                      <p className="text-sm font-medium text-green-800">{campaignCode}</p>
                    </div>
                    <p className="mt-1.5 text-sm text-green-700">
                      <strong>
                        {tc.benefitTrial
                          .replace('{d}', String(codeBenefit.trialDays))
                          .replace('{c}', String(codeBenefit.trialCredits))}
                      </strong>
                      <span className="text-green-600">
                        {' '}{tc.codeInstead}{' '}
                        {tc.benefitTrial
                          .replace('{d}', String(codeBenefit.baseTrialDays))
                          .replace('{c}', String(codeBenefit.baseTrialCredits))}
                      </span>
                    </p>
                    {(codeBenefit.discountPercent || Number(codeBenefit.purchaseCreditMultiplier ?? 1) > 1) && (
                      <p className="mt-1 text-sm text-green-700">
                        {tc.benefitDiscount}
                      </p>
                    )}
                  </div>
                )}

                {codeState === 'invalid' && codeMessage && (
                  <p className="mt-2 text-sm text-red-500">{codeMessage}</p>
                )}
              </div>
            )}
            </>
            )}

            {/* Volver al paso anterior sin perder lo escrito. */}
            {mode === 'signup' && signupPart === 'extras' && (
              <button
                type="button"
                onClick={() => setSignupPart('main')}
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4" />
                {tc.stepBack}
              </button>
            )}

            {/* Error toast rendered via portal at bottom of component */}

            <Button
              type="submit"
              className="mt-8! w-full h-11 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
              disabled={pending || uploadingLogo}
            >
              {pending || uploadingLogo ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  {uploadingLogo ? t.uploadingLogo : (mode === 'signin' ? t.signingIn : t.creatingAccount)}
                </>
              ) : (
                mode === 'signin'
                  ? t.signIn
                  : signupPart === 'main' ? tc.stepContinue : t.createAccount
              )}
            </Button>

            {(mode === 'signin' || signupPart === 'main') && !oauthPending && (
              <SocialAuthButtons
                mode={mode}
                labels={{
                  google: t.continueWithGoogle,
                  linkedin: t.continueWithLinkedIn,
                  orEmail: t.orEmailPassword,
                  googleOriginError: t.oauthGoogleOriginError,
                }}
                onBegin={handleOAuthBegin}
                onError={setError}
              />
            )}

          </form>

          {mode === 'signin' && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                className="text-sm text-purple-600 hover:text-purple-800 hover:underline cursor-pointer"
              >
                {t.forgotPassword}
              </button>
            </div>
          )}

          {/* Footer link */}
          {mode === 'signin' ? (
            <p className="mt-5 text-center text-sm text-gray-500">
              {t.noAccount}{' '}
              <Link
                href="/sign-up"
                className="font-medium text-gray-900 hover:underline"
              >
                {t.createAnAccount}
              </Link>
            </p>
          ) : (
            <p className="mt-5 text-center text-sm text-gray-500">
              {t.alreadyHaveAccount}{' '}
              <Link
                href="/sign-in"
                className="font-medium text-gray-900 hover:underline"
              >
                {t.signInLink}
              </Link>
            </p>
          )}

                    </div>
        </div>
      </div>

      {/* Right side - Premium Visual */}
      <div className="hidden lg:flex lg:w-[58%] xl:w-[62%] bg-gradient-to-br from-purple-800 via-purple-700 to-purple-800 items-center justify-center p-8 relative overflow-hidden">
{/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-violet-600 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-fuchsia-500 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

        {/* ORIGINAL FLOATING CARDS - START (commented for revert)
        <div className="relative z-10 max-w-lg w-full">
          <div className="relative h-80 mb-8">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 bg-white rounded-2xl shadow-2xl p-5 transform rotate-2 hover:rotate-0 transition-transform duration-500">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Birth Certificate</p>
                  <p className="text-xs text-gray-500">Spanish → English</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full w-full bg-gradient-to-r from-purple-500 to-violet-600 rounded-full" />
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-green-600 font-medium">Certified</span>
                  <span className="text-gray-400">100%</span>
                </div>
              </div>
            </div>
            <div className="absolute -left-4 top-4 w-48 bg-white/90 backdrop-blur-sm rounded-xl shadow-xl p-4 transform -rotate-6 hover:rotate-0 transition-transform duration-500">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="text-xs font-medium text-gray-700">USCIS Accepted</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-gray-900">99.8%</span>
                <span className="text-xs text-gray-500">accuracy</span>
              </div>
            </div>
            <div className="absolute -right-4 bottom-4 w-44 bg-white/90 backdrop-blur-sm rounded-xl shadow-xl p-4 transform rotate-6 hover:rotate-0 transition-transform duration-500">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs text-gray-500">Live translations</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-bold text-gray-900">2.4M+</span>
                <span className="text-xs text-gray-500">documents</span>
              </div>
            </div>
          </div>
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Translate with confidence</h2>
            <p className="text-purple-200 text-lg leading-relaxed mb-8">Join thousands of certified translators using AI-powered tools to deliver legally compliant translations faster.</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <p className="text-2xl font-bold text-white">50+</p>
                <p className="text-xs text-purple-300">Languages</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <p className="text-2xl font-bold text-white">24/7</p>
                <p className="text-xs text-purple-300">Support</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <p className="text-2xl font-bold text-white">SOC 2</p>
                <p className="text-xs text-purple-300">Certified</p>
              </div>
            </div>
          </div>
        </div>
        ORIGINAL FLOATING CARDS - END */}

        {/* Animated App Mockup - Cycling Screens */}
        <div className="relative z-10 w-full max-w-3xl translate-x-8 animate-[loginMockupFadeIn_1s_ease-out_both]">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
            {/* Browser chrome */}
            <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
              </div>
              <div className="flex-1 mx-8">
                <div className="bg-white rounded-md border border-gray-200 px-3 py-1 text-center">
                  <span className="text-[10px] text-gray-400 transition-opacity duration-300">
                    {mockupScreen === 0 && 'www.ezdocu.ai/orders'}
                    {mockupScreen === 1 && 'www.ezdocu.ai/workspace'}
                    {mockupScreen === 2 && 'www.ezdocu.ai/dashboard'}
                    {mockupScreen === 3 && 'www.ezdocu.ai/workspace/credits'}
                  </span>
                </div>
              </div>
            </div>

            {/* App layout */}
            <div className="flex h-[420px]">
              {/* Sidebar - changes active item based on screen */}
              <div className="w-[160px] border-r border-gray-100 bg-white p-3 flex flex-col">
                <div className="flex items-center gap-2 mb-5">
                  <Image src="/images/brand/ezdocuai-logo.png" alt="EZDocuAI" width={20} height={20} className="h-5 w-5" />
                  <span className="text-xs font-bold text-gray-900">EZDocuAI</span>
                </div>
                <div className="space-y-1">
                  {[
                    { icon: FileText, label: t.mockupNavOrders, active: mockupScreen === 0 },
                    { icon: Globe, label: t.mockupNavWorkspace, active: mockupScreen === 1 },
                    { icon: Users, label: t.mockupNavTeam, active: mockupScreen === 2 },
                    { icon: CreditCard, label: t.mockupNavCredits, active: mockupScreen === 3 },
                    { icon: Settings, label: t.mockupNavSettings, active: false },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-300 ${item.active ? 'bg-purple-50' : ''}`}>
                      <item.icon className={`w-3.5 h-3.5 transition-colors duration-300 ${item.active ? 'text-purple-600' : 'text-gray-400'}`} />
                      <span className={`text-[11px] transition-colors duration-300 ${item.active ? 'font-medium text-purple-700' : 'text-gray-500'}`}>{item.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-auto pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 px-1.5">
                    <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center">
                      <span className="text-[8px] font-bold text-purple-600">
                        {mockupScreen === 2 ? 'C' : 'A'}
                      </span>
                    </div>
                    <div>
                      <div className="text-[9px] font-medium text-gray-700">
                        {mockupScreen === 2 ? t.mockupCompanyAdmin : 'Sarah M.'}
                      </div>
                      <div className="text-[8px] text-gray-400">
                        {mockupScreen === 2 ? t.mockupBusinessPlan : t.mockupProPlan}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main content - cycles between screens */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Purple header */}
                <div className="bg-purple-600 px-4 py-2 flex items-center justify-between">
                  <span className="text-[10px] text-white/80 transition-opacity duration-300">
                    {mockupScreen === 0 && t.mockupHeaderOrders}
                    {mockupScreen === 1 && t.mockupHeaderWorkspace}
                    {mockupScreen === 2 && t.mockupHeaderCompanyDashboard}
                    {mockupScreen === 3 && t.mockupHeaderCreditsBilling}
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-white/15 rounded-full px-2 py-0.5">
                      <Zap className="w-2.5 h-2.5 text-yellow-300" />
                      <span className="text-[9px] text-white font-medium">
                        {mockupScreen === 2 ? `240 ${t.mockupCredits}` : `45 ${t.mockupCredits}`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Screen content - relative container for stacked screens */}
                <div className="flex-1 relative">
                  {/* Screen 0: Orders list */}
                  <div
                    className="absolute inset-0 flex flex-col transition-all duration-500 ease-in-out"
                    style={{
                      opacity: mockupScreen === 0 ? 1 : 0,
                      transform: mockupScreen === 0 ? 'translateY(0)' : mockupScreen > 0 ? 'translateY(-20px)' : 'translateY(20px)',
                      pointerEvents: mockupScreen === 0 ? 'auto' : 'none',
                    }}
                  >
                    <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
                      <div className="flex-1 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-100">
                        <span className="text-[9px] text-gray-400">{t.mockupSearchOrders}</span>
                      </div>
                      <div className="bg-purple-600 text-white text-[9px] px-3 py-1.5 rounded-lg font-medium">{t.mockupNewOrder}</div>
                    </div>
                    <div className="px-4 py-2 flex-1">
                      <div className="flex items-center gap-3 text-[8px] text-gray-400 uppercase tracking-wider pb-2 border-b border-gray-50 mb-1">
                        <span className="w-[30%]">{t.mockupColDocument}</span>
                        <span className="w-[20%]">{t.mockupColLanguage}</span>
                        <span className="w-[25%]">{t.mockupColStatus}</span>
                        <span className="w-[25%]">{t.mockupColDate}</span>
                      </div>
                      {[
                        { doc: t.mockupDocBirthCertificate, lang: 'ES → EN', status: t.mockupStatusCompleted, badge: 'bg-green-50 text-green-600' },
                        { doc: t.mockupDocMarriageLicense, lang: 'PT → EN', status: t.mockupStatusInReview, badge: 'bg-purple-50 text-purple-600' },
                        { doc: t.mockupDocDiploma, lang: 'FR → EN', status: t.mockupStatusProcessing, badge: 'bg-yellow-50 text-yellow-600' },
                        { doc: t.mockupDocMedicalRecord, lang: 'ES → EN', status: t.mockupStatusCompleted, badge: 'bg-green-50 text-green-600' },
                        { doc: t.mockupDocPowerOfAttorney, lang: 'ES → EN', status: t.mockupStatusPending, badge: 'bg-gray-50 text-gray-600' },
                      ].map((order, i) => (
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50">
                          <div className="w-[30%] flex items-center gap-1.5">
                            <FileText className="w-3 h-3 text-gray-400" />
                            <span className="text-[9px] font-medium text-gray-700 truncate">{order.doc}</span>
                          </div>
                          <span className="w-[20%] text-[9px] text-gray-500">{order.lang}</span>
                          <div className="w-[25%]">
                            <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${order.badge}`}>{order.status}</span>
                          </div>
                          <span className="w-[25%] text-[8px] text-gray-400">{mockupDate(10 + i, true)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Screen 1: Translation Editor */}
                  <div
                    className="absolute inset-0 flex flex-col transition-all duration-500 ease-in-out"
                    style={{
                      opacity: mockupScreen === 1 ? 1 : 0,
                      transform: mockupScreen === 1 ? 'translateY(0)' : mockupScreen > 1 ? 'translateY(-20px)' : 'translateY(20px)',
                      pointerEvents: mockupScreen === 1 ? 'auto' : 'none',
                    }}
                  >
                    <div className="bg-white border-b border-gray-100 px-3 py-2 flex items-center justify-center gap-1">
                      {[
                        { label: t.mockupStepUpload, done: true },
                        { label: t.mockupStepOcr, done: true },
                        { label: t.mockupStepReview, active: true },
                        { label: t.mockupStepExport, done: false },
                      ].map((step, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center ${step.done ? 'bg-green-500' : step.active ? 'bg-purple-600' : 'bg-gray-200'}`}>
                            {step.done ? <CheckCircle className="w-2.5 h-2.5 text-white" /> : <span className={`text-[7px] font-bold ${step.active ? 'text-white' : 'text-gray-400'}`}>{i + 1}</span>}
                          </div>
                          <span className={`text-[9px] ${step.active ? 'text-purple-700 font-medium' : step.done ? 'text-gray-500' : 'text-gray-400'}`}>{step.label}</span>
                          {i < 3 && <div className={`w-5 h-px mx-0.5 ${step.done ? 'bg-green-300' : 'bg-gray-200'}`} />}
                        </div>
                      ))}
                    </div>
                    <div className="flex-1 grid grid-cols-2 divide-x divide-gray-100">
                      <div className="p-4 bg-gray-50/50">
                        <div className="text-[9px] font-medium text-gray-400 uppercase tracking-wider mb-3">{t.mockupOriginalLabel}</div>
                        <div className="space-y-2">
                          {[0.75, 1, 0.85, 0.65, 0.8, 1, 0.7].map((w, i) => (
                            <div key={i} className="h-2.5 bg-gray-200 rounded" style={{ width: `${w * 100}%` }} />
                          ))}
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="text-[9px] font-medium text-purple-600 uppercase tracking-wider mb-3">{t.mockupTranslationLabel}</div>
                        <div className="space-y-2">
                          {[0.75, 1, 0.85, 0.65, 0.8, 1, 0.7].map((w, i) => (
                            <div key={i} className="h-2.5 bg-purple-100 rounded" style={{ width: `${w * 100}%` }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Screen 2: Company Dashboard */}
                  <div
                    className="absolute inset-0 flex flex-col p-4 transition-all duration-500 ease-in-out"
                    style={{
                      opacity: mockupScreen === 2 ? 1 : 0,
                      transform: mockupScreen === 2 ? 'translateY(0)' : mockupScreen > 2 ? 'translateY(-20px)' : 'translateY(20px)',
                      pointerEvents: mockupScreen === 2 ? 'auto' : 'none',
                    }}
                  >
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {[
                        { label: t.mockupTotalOrders, value: mockupNumber(1247), icon: FileText, trend: '+12%' },
                        { label: t.mockupTeamMembers, value: '8', icon: Users, trend: '+2' },
                        { label: t.mockupThisMonth, value: '89', icon: BarChart3, trend: '+23%' },
                      ].map((stat, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                          <div className="flex items-center justify-between mb-1">
                            <stat.icon className="w-3 h-3 text-gray-400" />
                            <span className="text-[8px] text-green-600 font-medium">{stat.trend}</span>
                          </div>
                          <div className="text-sm font-bold text-gray-900">{stat.value}</div>
                          <div className="text-[8px] text-gray-500">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="text-[9px] font-medium text-gray-500 uppercase tracking-wider mb-2">{t.mockupTeamActivity}</div>
                    {[
                      { name: 'Maria G.', role: t.mockupRoleTranslator, orders: 23, color: 'bg-pink-100 text-pink-600' },
                      { name: 'John D.', role: t.mockupRoleReviewer, orders: 15, color: 'bg-blue-100 text-blue-600' },
                      { name: 'Ana L.', role: t.mockupRoleTranslator, orders: 31, color: 'bg-emerald-100 text-emerald-600' },
                      { name: 'Carlos R.', role: t.mockupRoleAdmin, orders: 12, color: 'bg-orange-100 text-orange-600' },
                    ].map((member, i) => (
                      <div key={i} className="flex items-center gap-2 py-1.5 border-b border-gray-50">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold ${member.color}`}>{member.name[0]}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] font-medium text-gray-700">{member.name}</div>
                          <div className="text-[8px] text-gray-400">{member.role}</div>
                        </div>
                        <div className="text-[9px] text-gray-500">{member.orders} {t.mockupRowOrders}</div>
                      </div>
                    ))}
                  </div>

                  {/* Screen 3: Credits & Billing */}
                  <div
                    className="absolute inset-0 flex flex-col p-4 transition-all duration-500 ease-in-out"
                    style={{
                      opacity: mockupScreen === 3 ? 1 : 0,
                      transform: mockupScreen === 3 ? 'translateY(0)' : 'translateY(20px)',
                      pointerEvents: mockupScreen === 3 ? 'auto' : 'none',
                    }}
                  >
                    <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-3 border border-purple-100 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[9px] font-medium text-purple-700">{t.mockupAvailableCredits}</span>
                        <span className="text-[8px] text-purple-500 font-medium px-1.5 py-0.5 bg-purple-100 rounded-full">{t.mockupProPlan}</span>
                      </div>
                      <div className="text-xl font-bold text-purple-900 mb-1">45 <span className="text-[10px] font-normal text-purple-500">/ 100</span></div>
                      <div className="h-1.5 bg-purple-200 rounded-full overflow-hidden">
                        <div className="h-full w-[45%] bg-gradient-to-r from-purple-500 to-violet-500 rounded-full" />
                      </div>
                    </div>
                    <div className="text-[9px] font-medium text-gray-500 uppercase tracking-wider mb-2">{t.mockupRecentTransactions}</div>
                    {[
                      { desc: t.mockupTxBirthCertificate, credits: -3, date: mockupDate(14), icon: FileText },
                      { desc: t.mockupTxCreditPurchase.replace('{n}', '50'), credits: 50, date: mockupDate(12), icon: CreditCard },
                      { desc: t.mockupTxDiplomaOcr, credits: -5, date: mockupDate(10), icon: FileText },
                      { desc: t.mockupDocMarriageLicense, credits: -3, date: mockupDate(8), icon: FileText },
                      { desc: t.mockupTxCreditPurchase.replace('{n}', '100'), credits: 100, date: mockupDate(1), icon: CreditCard },
                    ].map((tx, i) => (
                      <div key={i} className="flex items-center gap-2 py-1.5 border-b border-gray-50">
                        <div className={`w-5 h-5 rounded-lg flex items-center justify-center ${tx.credits > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                          <tx.icon className={`w-2.5 h-2.5 ${tx.credits > 0 ? 'text-green-500' : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] font-medium text-gray-700 truncate">{tx.desc}</div>
                          <div className="text-[8px] text-gray-400">{tx.date}</div>
                        </div>
                        <span className={`text-[9px] font-bold ${tx.credits > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                          {tx.credits > 0 ? '+' : ''}{tx.credits}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Screen description text */}
          <div className="mt-5 text-center relative h-[52px]">
            {[
              { title: t.mockupTitleOrders, desc: t.mockupDescOrders },
              { title: t.mockupTitleEditor, desc: t.mockupDescEditor },
              { title: t.mockupTitleTeam, desc: t.mockupDescTeam },
              { title: t.mockupTitleCredits, desc: t.mockupDescCredits },
            ].map((item, i) => (
              <div
                key={i}
                className="absolute inset-0 transition-all duration-500 ease-in-out"
                style={{
                  opacity: mockupScreen === i ? 1 : 0,
                  transform: mockupScreen === i ? 'translateY(0)' : 'translateY(10px)',
                }}
              >
                <h3 className="text-base font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-purple-200">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Screen indicator dots */}
          <div className="flex justify-center gap-2 mt-4">
            {[t.mockupTabOrders, t.mockupTabEditor, t.mockupTabTeam, t.mockupTabCredits].map((label, i) => (
              <button
                key={i}
                onClick={() => setMockupScreen(i)}
                className={`transition-all duration-300 rounded-full ${
                  mockupScreen === i
                    ? 'bg-white px-4 py-1.5'
                    : 'bg-white/30 hover:bg-white/50 px-3 py-1.5'
                }`}
              >
                <span className={`text-[11px] font-medium transition-colors duration-300 ${
                  mockupScreen === i ? 'text-purple-700' : 'text-white/70'
                }`}>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Signup email verification (OTP) modal */}
      {mode === 'signup' && signupStep === 'code' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeSignupCode} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8">
            <button
              type="button"
              onClick={closeSignupCode}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label={t.backToSignIn}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t.verifyEmailTitle}
              </h2>
              <p className="text-gray-500 mt-2 text-sm">
                {t.verifyEmailDesc}<br />
                <span className="font-medium text-gray-900">{signupEmail}</span>
              </p>
            </div>

            <div className="mb-4">
              <Label className="text-sm font-medium text-gray-700 mb-3 block">
                {t.verificationCode}
              </Label>
              <div className="flex gap-2 justify-between">
                {signupCode.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="text"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleSignupCodeChange(index, e.target.value)}
                    className="w-12 h-14 text-center text-xl font-semibold border border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all"
                  />
                ))}
              </div>
              <div className="flex items-center justify-end mt-3">
                <button
                  type="button"
                  onClick={handleResendSignupCode}
                  disabled={signupCooldown > 0 || signupPending}
                  className="text-sm font-medium text-purple-600 hover:text-purple-700 disabled:text-gray-400 flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  {signupCooldown > 0 ? t.resendIn.replace('{n}', String(signupCooldown)) : t.resendCode}
                </button>
              </div>
            </div>

            {signupError && (
              <div className="mb-4 bg-purple-50 border border-purple-200 text-purple-700 px-4 py-3 rounded-lg text-sm">
                {signupError}
              </div>
            )}

            <Button
              type="button"
              onClick={handleVerifySignup}
              disabled={signupPending}
              className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
            >
              {signupPending ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  {t.creatingAccount}
                </>
              ) : (
                t.verifyEmailCta
              )}
            </Button>

            <p className="mt-6 text-center text-sm text-gray-500">
              <button
                type="button"
                onClick={closeSignupCode}
                className="font-medium text-purple-600 hover:text-purple-700"
              >
                {t.verifyEmailWrong}
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={resetForgotModal}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8">
            {/* Close button */}
            <button
              type="button"
              onClick={resetForgotModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Step 1: Email */}
            {forgotStep === 'email' && (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-6 h-6 text-purple-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t.forgotPasswordTitle}
                  </h2>
                  <p className="text-gray-500 mt-2 text-sm">
                    {t.forgotPasswordDesc}
                  </p>
                </div>

                <form onSubmit={handleSendCode}>
                  <div className="mb-4">
                    <Label htmlFor="forgotEmail" className="text-xs font-medium text-gray-700">
                      {t.emailAddress}
                    </Label>
                    <Input
                      id="forgotEmail"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="mt-2 h-12 rounded-lg border-gray-200 focus:border-purple-500 focus:ring-purple-500"
                      placeholder={t.emailPlaceholder}
                      autoFocus
                    />
                  </div>

                  {forgotError && (
                    <div className="mb-4 bg-purple-50 border border-purple-200 text-purple-700 px-4 py-3 rounded-lg text-sm">
                      {forgotError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
                    disabled={forgotPending}
                  >
                    {forgotPending ? (
                      <>
                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                        {t.sendingCode}
                      </>
                    ) : (
                      t.sendCode
                    )}
                  </Button>
                </form>

                <p className="mt-6 text-center text-sm text-gray-500">
                  <button
                    type="button"
                    onClick={resetForgotModal}
                    className="font-medium text-purple-600 hover:text-purple-700"
                  >
                    {t.backToSignIn}
                  </button>
                </p>
              </>
            )}

            {/* Step 2: Enter Code */}
            {forgotStep === 'code' && (
              <>
                {/* Back button */}
                <button
                  type="button"
                  onClick={() => setForgotStep('email')}
                  className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 flex items-center gap-1 text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>

                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-6 h-6 text-purple-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t.verificationCode}
                  </h2>
                  <p className="text-gray-500 mt-2 text-sm">
                    {t.verificationCodeDesc}<br />
                    <span className="font-medium text-gray-900">{forgotEmail}</span>
                  </p>
                </div>

                <div className="mb-4">
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">
                    {t.verificationCode}
                  </Label>
                  <div className="flex gap-2 justify-between">
                    {forgotCode.map((digit, index) => (
                      <input
                        key={index}
                        type="text"
                        inputMode="text"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        className="w-12 h-14 text-center text-xl font-semibold border border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all"
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm text-gray-500"></span>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resendCooldown > 0 || forgotPending}
                      className="text-sm font-medium text-purple-600 hover:text-purple-700 disabled:text-gray-400 flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" />
                      {resendCooldown > 0 ? t.resendIn.replace('{n}', String(resendCooldown)) : t.resendCode}
                    </button>
                  </div>
                </div>

                {forgotError && (
                  <div className="mb-4 bg-purple-50 border border-purple-200 text-purple-700 px-4 py-3 rounded-lg text-sm">
                    {forgotError}
                  </div>
                )}

                <Button
                  type="button"
                  onClick={handleVerifyCode}
                  className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
                >
                  {t.verifyCode}
                </Button>
              </>
            )}

            {/* Step 3: New Password */}
            {forgotStep === 'password' && (
              <>
                {/* Back button */}
                <button
                  type="button"
                  onClick={() => setForgotStep('code')}
                  className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 flex items-center gap-1 text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>

                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <KeyRound className="w-6 h-6 text-purple-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t.newPassword}
                  </h2>
                  <p className="text-gray-500 mt-2 text-sm">
                    {t.forgotPasswordDesc}
                  </p>
                </div>

                <form onSubmit={handleResetPassword}>
                  <div className="mb-4">
                    <Label htmlFor="newPassword" className="text-xs font-medium text-gray-700">
                      {t.newPassword}
                    </Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showForgotPassword ? 'text' : 'password'}
                        value={forgotNewPassword}
                        onChange={(e) => setForgotNewPassword(e.target.value)}
                        required
                        minLength={8}
                        className="mt-2 h-12 rounded-lg border-gray-200 focus:border-purple-500 focus:ring-purple-500 pr-12"
                        placeholder={t.passwordPlaceholder}
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(!showForgotPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-gray-400 hover:text-gray-600"
                      >
                        {showForgotPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <Label htmlFor="confirmNewPassword" className="text-xs font-medium text-gray-700">
                      {t.confirmPassword}
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmNewPassword"
                        type={showForgotConfirmPassword ? 'text' : 'password'}
                        value={forgotConfirmPassword}
                        onChange={(e) => setForgotConfirmPassword(e.target.value)}
                        required
                        minLength={8}
                        className="mt-2 h-12 rounded-lg border-gray-200 focus:border-purple-500 focus:ring-purple-500 pr-12"
                        placeholder={t.confirmPasswordPlaceholder}
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotConfirmPassword(!showForgotConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-gray-400 hover:text-gray-600"
                      >
                        {showForgotConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {forgotError && (
                    <div className="mb-4 bg-purple-50 border border-purple-200 text-purple-700 px-4 py-3 rounded-lg text-sm">
                      {forgotError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
                    disabled={forgotPending}
                  >
                    {forgotPending ? (
                      <>
                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                        {t.resettingPassword}
                      </>
                    ) : (
                      t.resetPassword
                    )}
                  </Button>
                </form>
              </>
            )}

            {/* Step 4: Success */}
            {forgotStep === 'success' && (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {t.passwordResetSuccess}
                </h2>
                <p className="text-gray-500 mb-6">
                  {t.passwordResetSuccessDesc}
                </p>
                <Button
                  type="button"
                  onClick={resetForgotModal}
                  className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
                >
                  {t.backToSignIn}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Error Toast Notification */}
      {error && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="flex items-start gap-3 px-5 py-3.5 bg-white border border-red-200 rounded-xl shadow-lg shadow-red-100/50 min-w-[320px] max-w-lg">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-sm font-medium text-gray-900 flex-1 leading-snug">{error}</p>
            <button type="button" onClick={() => setError('')} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
